import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '@shared/api/http';
import { defaultApi } from '@shared/config/runtime';
import { loadAppConfig } from '@domain/appConfig/client';
import { preloadAudioTranscoder } from '@shared/utils/audioTranscode';
import type { AppConfig } from '@domain/appConfig/types';
import { loadInitialAuthState } from '@domain/auth/useAuthSession';
import type { AuthSessionState } from '@domain/auth/types';
import {
  getAutoRetryDelay,
  getExitDuration,
  resolveStartupPhase,
  startupTimings,
  type BootstrapFailure,
} from './startupPresentation';

type BootstrapResult = {
  appConfig: AppConfig;
  authState: AuthSessionState;
};

type ActiveAttempt = {
  id: number;
  controller: AbortController;
  manual: boolean;
  timeoutId: number;
};

function markOnce(name: string, startTime?: number) {
  if (typeof performance === 'undefined' || performance.getEntriesByName(name).length > 0) return;
  try {
    performance.mark(name, startTime == null ? undefined : { startTime });
  } catch {
    // Startup instrumentation must never block App entry.
  }
}

function measureOnce(name: string, startMark: string, endMark: string) {
  if (typeof performance === 'undefined' || performance.getEntriesByName(name).length > 0) return;
  try {
    performance.measure(name, startMark, endMark);
  } catch {
    // Missing/unsupported marks never affect startup.
  }
}

function classifyFailure(cause: unknown, attemptId: number, elapsedMs: number): BootstrapFailure {
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return { kind: 'abort', cause, attemptId, elapsedMs };
  }
  if (cause instanceof DOMException && cause.name === 'TimeoutError') {
    const kind = cause.message.includes('attempt') ? 'attempt-timeout' : 'request-timeout';
    return { kind, cause, attemptId, elapsedMs };
  }
  if (isApiError(cause)) {
    return { kind: 'http', cause, attemptId, elapsedMs, status: cause.status };
  }
  if (cause instanceof TypeError) {
    return { kind: 'network', cause, attemptId, elapsedMs };
  }
  if (cause instanceof SyntaxError) {
    return { kind: 'parse', cause, attemptId, elapsedMs };
  }
  return { kind: 'unknown', cause, attemptId, elapsedMs };
}

export function useStartupBootstrap() {
  const [nowMs, setNowMs] = useState(() => performance.now());
  const [result, setResult] = useState<BootstrapResult | null>(null);
  const [visibilityOpportunityAt, setVisibilityOpportunityAt] = useState<number | null>(null);
  const [exitComplete, setExitComplete] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [offlineHint, setOfflineHint] = useState(false);
  const [failure, setFailure] = useState<BootstrapFailure | null>(null);
  const offlineHintRef = useRef(false);
  const activeAttemptRef = useRef<ActiveAttempt | null>(null);
  const attemptIdRef = useRef(0);
  const failureCountRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const startAttemptRef = useRef<(manual: boolean) => void>(() => undefined);

  const reducedMotion = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );
  const exitDurationMs = getExitDuration(reducedMotion);
  const phase = resolveStartupPhase({
    nowMs,
    ready: result != null,
    visibilityOpportunityAt,
    exitComplete,
  });

  useEffect(() => {
    markOnce('locs:startup-start', 0);
    let disposed = false;

    const clearRetryTimer = () => {
      if (retryTimerRef.current != null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const startAttempt = (manual: boolean) => {
      if (disposed) return;
      clearRetryTimer();
      if (activeAttemptRef.current) window.clearTimeout(activeAttemptRef.current.timeoutId);
      activeAttemptRef.current?.controller.abort(
        new DOMException('Bootstrap attempt superseded', 'AbortError'),
      );
      const id = ++attemptIdRef.current;
      const controller = new AbortController();
      setRetrying(manual);
      setFailure(null);
      const timeout = window.setTimeout(() => {
        controller.abort(new DOMException('Bootstrap attempt timed out', 'TimeoutError'));
      }, startupTimings.attemptTimeoutMs);
      activeAttemptRef.current = { id, controller, manual, timeoutId: timeout };

      const appConfigPromise = loadAppConfig(defaultApi, { signal: controller.signal }).then((appConfig) => {
        void preloadAudioTranscoder().catch((error: unknown) => {
          console.warn('[audio] ffmpeg preload failed', error);
        });
        return appConfig;
      });
      void Promise.all([
        appConfigPromise,
        loadInitialAuthState(defaultApi, controller.signal),
      ]).then(([appConfig, authState]) => {
        window.clearTimeout(timeout);
        if (disposed || activeAttemptRef.current?.id !== id) return;
        activeAttemptRef.current = null;
        failureCountRef.current = 0;
        setRetrying(false);
        offlineHintRef.current = false;
        setOfflineHint(false);
        setResult({ appConfig, authState });
        markOnce('locs:bootstrap-ready');
        measureOnce('locs:bootstrap-duration', 'locs:startup-start', 'locs:bootstrap-ready');
        setNowMs(performance.now());
      }).catch((cause: unknown) => {
        window.clearTimeout(timeout);
        if (disposed || activeAttemptRef.current?.id !== id) return;
        activeAttemptRef.current = null;
        controller.abort(cause);
        setRetrying(false);
        const nextFailure = classifyFailure(cause, id, performance.now());
        if (nextFailure.kind === 'abort') return;
        setFailure(nextFailure);
        setNowMs(performance.now());
        const isOfflineNetworkFailure = nextFailure.kind === 'network' && navigator.onLine === false;
        offlineHintRef.current = isOfflineNetworkFailure;
        setOfflineHint(isOfflineNetworkFailure);
        if (!isOfflineNetworkFailure) {
          failureCountRef.current += 1;
          retryTimerRef.current = window.setTimeout(
            () => startAttempt(false),
            getAutoRetryDelay(failureCountRef.current),
          );
        }
      });
    };

    startAttemptRef.current = startAttempt;
    const handleOnline = () => {
      if (!disposed && !activeAttemptRef.current && offlineHintRef.current) startAttempt(false);
    };
    window.addEventListener('online', handleOnline);
    startAttempt(false);

    return () => {
      disposed = true;
      clearRetryTimer();
      if (activeAttemptRef.current) window.clearTimeout(activeAttemptRef.current.timeoutId);
      activeAttemptRef.current?.controller.abort(
        new DOMException('Bootstrap controller disposed', 'AbortError'),
      );
      activeAttemptRef.current = null;
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const boundaries: number[] = [
      startupTimings.revealDelayMs,
      startupTimings.longWaitMs,
      startupTimings.recoveryWaitMs,
    ];
    if (result && visibilityOpportunityAt != null) {
      boundaries.push(visibilityOpportunityAt + startupTimings.minimumVisibleMs);
    }
    const next = boundaries.filter((boundary) => boundary > nowMs).sort((a, b) => a - b)[0];
    if (next == null) return undefined;
    const timer = window.setTimeout(() => setNowMs(performance.now()), Math.max(0, next - nowMs));
    return () => window.clearTimeout(timer);
  }, [nowMs, result, visibilityOpportunityAt]);

  useEffect(() => {
    if (phase !== 'exiting') return undefined;
    const timer = window.setTimeout(() => {
      setExitComplete(true);
    }, exitDurationMs);
    return () => window.clearTimeout(timer);
  }, [exitDurationMs, phase]);

  const onVisibilityOpportunity = useCallback((timestamp: number) => {
    setVisibilityOpportunityAt((current) => {
      if (current != null) return current;
      markOnce('locs:loading-visible');
      return timestamp;
    });
    setNowMs(timestamp);
  }, []);

  const retry = useCallback(() => startAttemptRef.current(true), []);
  const onAppInteractive = useCallback(() => {
    markOnce('locs:app-interactive');
    measureOnce('locs:startup-interactive-duration', 'locs:startup-start', 'locs:app-interactive');
    if (performance.getEntriesByName('locs:loading-visible').length > 0) {
      measureOnce('locs:loading-visible-duration', 'locs:loading-visible', 'locs:app-interactive');
    }
  }, []);

  return {
    phase,
    result,
    retrying,
    offlineHint,
    failure,
    exitDurationMs,
    onVisibilityOpportunity,
    onAppInteractive,
    retry,
  };
}
